import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  deleteAssistantID,
  getAllAssistant,
  getAssistantFetchExternal,
  updateAssistant,
} from "../../utils/api/assistantAPI";

const initialState = {
  allAssistantValue: [],
  value: [],
  status: "idle",
  error: null,
  activeDetail: {},
  updateData: {},
  detailStatus: "idle",
  detailError: null,
};

/**
 * Fetch all assistants scoped to a specific organization (userOrgId).
 */
export const fetchAssistants = createAsyncThunk(
  "assistants/fetchAssistants",
  async (userOrgId, { rejectWithValue }) => {
    try {
      const data = await getAllAssistant(userOrgId);
      return data?.assistants || [];
    } catch (error) {
      return rejectWithValue(error?.message || "Failed to fetch assistants");
    }
  }
);

/**
 * Fetch all assistants (no org filter).
 * NOTE: name kept as-is to match your current codebase.
 */
export const fetchAllAssistat = createAsyncThunk(
  "assistants/fetchAllAssistants",
  async (_, { rejectWithValue }) => {
    try {
      const data = await getAllAssistant();
      return data?.assistants || [];
    } catch (error) {
      return rejectWithValue(error?.message || "Failed to fetch assistants");
    }
  }
);

/**
 * Fetch a specific assistant’s details.
 */
export const fetchAssistantDetail = createAsyncThunk(
  "assistants/fetchAssistantDetail",
  async (assistantId, { rejectWithValue }) => {
    try {
      const detail = await getAssistantFetchExternal(assistantId);
      return detail || {};
    } catch (error) {
      return rejectWithValue(
        error?.message || "Failed to fetch assistant detail"
      );
    }
  }
);

/**
 * Update assistant and refresh detail + (optionally) list.
 */
export const saveAssistantDetail = createAsyncThunk(
  "assistants/updateAssistantDetail",
  async ({ assistantId, userOrgId }, { rejectWithValue, getState, dispatch }) => {
    try {
      const { updateData } = getState().assistants;

      const response = await updateAssistant(assistantId, updateData);

      // Refresh detail after update
      await dispatch(fetchAssistantDetail(assistantId));

      // If your list depends on org context, refresh it too
      if (typeof userOrgId !== "undefined") {
        await dispatch(fetchAssistants(userOrgId));
      }

      return response;
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to update assistant");
    }
  }
);

/**
 * Delete an assistant.
 */
export const deleteAssistant = createAsyncThunk(
  "assistants/deleteAssistant",
  async ({ assistantId }, { rejectWithValue }) => {
    try {
      const response = await deleteAssistantID(assistantId);
      return response;
    } catch (e) {
      return rejectWithValue(e?.message || "Failed to delete assistant");
    }
  }
);

const assistantSlice = createSlice({
  name: "assistants",
  initialState,
  reducers: {
    appendToAssistantDetail: (state, action) => {
      state.updateData = {
        ...state.updateData,
        ...action.payload,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      // -------- Fetch Assistants (org-scoped) --------
      .addCase(fetchAssistants.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchAssistants.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.value = action.payload;
      })
      .addCase(fetchAssistants.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || action.error.message;
      })

      // -------- Fetch All Assistants (no org) --------
      .addCase(fetchAllAssistat.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchAllAssistat.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.allAssistantValue = action.payload;
      })
      .addCase(fetchAllAssistat.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || action.error.message;
      })

      // -------- Fetch Assistant Detail --------
      .addCase(fetchAssistantDetail.pending, (state) => {
        state.detailStatus = "loading";
        state.detailError = null;
      })
      .addCase(fetchAssistantDetail.fulfilled, (state, action) => {
        state.detailStatus = "succeeded";
        state.activeDetail = action.payload || {};
      })
      .addCase(fetchAssistantDetail.rejected, (state, action) => {
        state.detailStatus = "failed";
        state.detailError = action.payload || action.error.message;
      })

      // -------- Save Assistant Detail --------
      .addCase(saveAssistantDetail.pending, (state) => {
        state.detailStatus = "saving";
        state.detailError = null;
      })
      .addCase(saveAssistantDetail.fulfilled, (state, action) => {
        state.detailStatus = "saved";
        // If your API returns updated fields under `details`,
        // keep the merge to reflect latest server state.
        state.activeDetail = {
          ...state.activeDetail,
          ...(action.payload?.details || {}),
        };
        // Clear staged updates if you prefer:
        // state.updateData = {};
      })
      .addCase(saveAssistantDetail.rejected, (state, action) => {
        state.detailStatus = "failed";
        state.detailError = action.payload || action.error.message;
      })

      // -------- Delete Assistant --------
      .addCase(deleteAssistant.pending, (state) => {
        state.status = "deleting";
        state.error = null;
      })
      .addCase(deleteAssistant.fulfilled, (state, action) => {
        state.status = "deleted";
        // Remove deleted assistant from list
        state.value = state.value.filter(
          (item) => item.id !== action.meta.arg.assistantId
        );
        // If the deleted assistant is currently active, clear it
        if (state.activeDetail?.id === action.meta.arg.assistantId) {
          state.activeDetail = {};
        }
      })
      .addCase(deleteAssistant.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || action.error.message;
      });
  },
});

export const { appendToAssistantDetail } = assistantSlice.actions;
export default assistantSlice.reducer;
